import dayjs from "dayjs";
import { MdClose } from "react-icons/md";
import { DATE_FORMAT } from "utils/constants";
import {
  DATE_PRESETS_KEYS,
  FIELD_TYPES,
  getDatePresetRange,
  getFieldConfig,
} from "./config";

const isKnownDatePresetKey = (key) =>
  Boolean(key) && DATE_PRESETS_KEYS.includes(key);

/** Label for relative date presets; only when key is declared in DATE_PRESETS_KEYS. */
const resolveDatePresetLabel = (filter, translate) => {
  if (!isKnownDatePresetKey(filter.datePresetKey)) return null;
  return translate.DATE_PRESETS?.[filter.datePresetKey] ?? filter.datePresetKey;
};

const dateRangeSeparator = (translate) =>
  translate.DATE_RANGE_SEPARATOR ?? " et ";

const formatTemplateWithCount = (template, count) => {
  if (!template) return String(count);
  return String(template).replace("{{count}}", String(count));
};

const resolveValueLabel = (rawValue, fieldKey, selectionOptions) => {
  const options = selectionOptions?.[fieldKey];
  if (!options) return rawValue;
  const match = options.find((opt) => opt.value === rawValue);
  return match?.label || rawValue;
};

const formatChipLabel = (filter, translate, selectionOptions, fields) => {
  const fieldConfig = getFieldConfig(filter.fieldKey, fields);
  if (!fieldConfig) return "";

  const fieldLabel = translate.FIELDS[fieldConfig.labelKey];
  const operator = filter.operator;

  if (operator === "defined")
    return `${fieldLabel} : ${translate.OPERATORS.DEFINED}`;
  if (operator === "not_defined")
    return `${fieldLabel} : ${translate.OPERATORS.NOT_DEFINED}`;

  if (operator === "is" || operator === "is_not") {
    if (fieldConfig.type === FIELD_TYPES.DATE) {
      const isDateOpLabelIsNot =
        translate.CHIP_DATE_IS_NOT_OPERATOR ??
        translate.OPERATORS.IS_NOT ??
        translate.OPERATORS.IS_DATE;
      const isDateOpLabel =
        operator === "is_not"
          ? isDateOpLabelIsNot
          : (translate.CHIP_DATE_IS_OPERATOR ?? translate.OPERATORS.IS_DATE);
      if (isKnownDatePresetKey(filter.datePresetKey)) {
        const [r0] = getDatePresetRange(filter.datePresetKey);
        const presetLabel = resolveDatePresetLabel(filter, translate);
        const resolved = r0.format(DATE_FORMAT);
        return `${fieldLabel} ${isDateOpLabel} ${presetLabel} (${resolved})`;
      }
      const formatted = filter.value
        ? dayjs(filter.value).format(DATE_FORMAT)
        : "";
      return `${fieldLabel} ${isDateOpLabel} ${formatted}`;
    }
    const valuesDisplay = (filter.values || [])
      .map((v) => resolveValueLabel(v, filter.fieldKey, selectionOptions))
      .join(", ");
    return `${fieldLabel} : ${valuesDisplay}`;
  }

  const operatorLabel =
    translate.OPERATORS[
      Object.keys(translate.OPERATORS).find(
        (k) => k === operator?.toUpperCase(),
      )
    ] || operator;

  const isDate = fieldConfig.type === FIELD_TYPES.DATE;
  const formatVal = (v) =>
    isDate && v ? dayjs(v).format(DATE_FORMAT) : v || "";

  if (operator === "between") {
    const sep = dateRangeSeparator(translate);
    if (isKnownDatePresetKey(filter.datePresetKey)) {
      const [r0, r1] = getDatePresetRange(filter.datePresetKey);
      return `${fieldLabel} ${operatorLabel} ${r0.format(DATE_FORMAT)}${sep}${r1.format(DATE_FORMAT)}`;
    }
    return `${fieldLabel} ${operatorLabel} ${formatVal(filter.value)}${sep}${formatVal(filter.value2)}`;
  }

  if (
    isDate &&
    isKnownDatePresetKey(filter.datePresetKey) &&
    (operator === "before" || operator === "after")
  ) {
    const [r0] = getDatePresetRange(filter.datePresetKey);
    const presetLabel = resolveDatePresetLabel(filter, translate);
    const resolved = r0.format(DATE_FORMAT);
    return `${fieldLabel} ${operatorLabel} ${presetLabel} (${resolved})`;
  }

  return `${fieldLabel} ${operatorLabel} ${formatVal(filter.value)}`;
};

const FilterChips = ({
  appliedFilters,
  onRemoveFilter,
  translate,
  resultsCount,
  selectionOptions,
  fields,
}) => {
  if (!appliedFilters || appliedFilters.length === 0) return null;

  return (
    <>
      <div className="advanced-filter__chips">
        <span className="advanced-filter__chips-label">
          {translate.ACTIVE_FILTERS}
        </span>
        {appliedFilters.map((filter, index) => (
          <span
            key={`chip-${filter.fieldKey}-${index}`}
            className="advanced-filter__chip"
          >
            {formatChipLabel(filter, translate, selectionOptions, fields)}
            <MdClose
              className="anticon-close"
              size={12}
              onClick={(e) => {
                e.stopPropagation();
                onRemoveFilter(index);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") onRemoveFilter(index);
              }}
            />
          </span>
        ))}
      </div>
      {resultsCount != null && (
        <span className="advanced-filter__results-count">
          {formatTemplateWithCount(translate.RESULTS_FOUND, resultsCount)}
        </span>
      )}
    </>
  );
};

export default FilterChips;
